/**
 * AssignShiftDialog — Assign an open shift to a staff member, or delete it
 */

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { Staff } from '../../stores/workforce-store';
import type { ScheduleEntry } from '../../stores/schedule-store';

const shiftLabels: Record<string, string> = {
  wasserij: 'Wasserij',
  route: 'Route',
  kantoor: 'Kantoor',
};

interface AssignShiftDialogProps {
  shift: ScheduleEntry;
  staffList: Staff[];
  onAssign: (staffId: string) => void;
  onDelete: () => void;
  onClose: () => void;
  isSaving: boolean;
}

export function AssignShiftDialog({ shift, staffList, onAssign, onDelete, onClose, isSaving }: AssignShiftDialogProps) {
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  const dateLabel = format(parseISO(shift.date), 'EEEE d MMMM', { locale: nl });
  const label = shift.shift_type ? shiftLabels[shift.shift_type] : 'Dienst';
  const activeStaff = staffList.filter(s => s.is_active);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <h3 className="text-lg font-semibold text-main mb-1">
          {label} toewijzen
        </h3>
        <p className="text-sm text-muted mb-4">
          {dateLabel} &middot; {shift.time_from?.slice(0, 5)}-{shift.time_to?.slice(0, 5)}
        </p>

        {/* Staff list */}
        <div className="flex-1 overflow-y-auto -mx-2 px-2 mb-4 min-h-0">
          <p className="text-xs font-medium text-muted mb-2">Toewijzen aan:</p>
          <div className="space-y-1">
            {activeStaff.map(staff => {
              const isSelected = selectedStaffId === staff.id;
              return (
                <button
                  key={staff.id}
                  onClick={() => setSelectedStaffId(staff.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 border border-indigo-300'
                      : 'hover:bg-slate-50 border border-transparent'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-main">{staff.name.split(' ')[0]}</span>
                    <span className="text-[10px] text-muted ml-2">{staff.team || 'algemeen'}</span>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    isSelected ? 'border-indigo-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-indigo-500" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => selectedStaffId && onAssign(selectedStaffId)}
            disabled={isSaving || !selectedStaffId}
            className="flex-1 py-2.5 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
          >
            {isSaving ? 'Bezig...' : 'Toewijzen'}
          </button>
          <button
            onClick={onDelete}
            disabled={isSaving}
            className="px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Verwijder
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm transition-colors"
          >
            Annuleren
          </button>
        </div>
      </div>
    </div>
  );
}
